import { CommonModule } from "@angular/common";
import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  OnInit,
} from "@angular/core";
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatCheckboxModule } from "@angular/material/checkbox";
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogModule,
  MatDialogRef,
  MatDialogTitle,
} from "@angular/material/dialog";
import { MatDividerModule } from "@angular/material/divider";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule, MatSelectChange } from "@angular/material/select";
import { MatTooltip } from "@angular/material/tooltip";
import { NgxMatSelectSearchModule } from "ngx-mat-select-search";
import {
  emailLengthValidator,
  uniqueEmailValidator,
} from "@directives/user-admin/email-validator.directive";
import { User, UserModalData } from "@models/user.model";
import { Organization } from "@models/organization.model";
import { Actions, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { ModalStateService } from "@services/modal-state.service";
import { UserAdminService } from "@services/user-admin.service";
import { ClinicianDetailsActions } from "@store/actions/clinician-details/clinician-details.actions";
import { UserAdminActions } from "@store/actions/user-admin/user-admin.actions";
import {
  selectIsSaveDisabled,
  selectUserAdminFilteredOrgs,
  selectUserRoles,
  selectUserAdminFilteredOrgsWithRole,
  selectUserAdminFilteredRolesWithRole
} from "@store/selectors/user-admin/user-admin.selector";
import { selectOrgId } from "@store/selectors/organization-emr-details/organization-emr-details.selector";
import { Observable, Subscription, combineLatest } from "rxjs";
import { map, startWith, filter, take } from "rxjs/operators";
import { MatFormFieldModule } from "@angular/material/form-field";
import { ConfirmationModalComponent } from "@components/confirmation-modal/confirmation-modal.component";

interface UserForm {
  firstName: FormControl<string>;
  lastName: FormControl<string>;
  role: FormControl<string | number | null>;
  emailAddress: FormControl<string>;
  org: FormControl<number | number[] | null>;
  active: FormControl<boolean>;
}

@Component({
  selector: "app-edit-add-user-modal",
  imports: [
    ReactiveFormsModule,
    CommonModule,
    FormsModule,
    MatInputModule,
    MatSelectModule,
    MatFormFieldModule,
    MatDividerModule,
    MatDialogModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    MatButtonModule,
    MatTooltip,
    MatCheckboxModule,
    NgxMatSelectSearchModule,
  ],
  templateUrl: "./edit-add-user-modal.component.html",
  styleUrl: "./edit-add-user-modal.component.css",
})
export class EditAddUserModalComponent
  implements OnInit, OnDestroy, AfterViewInit, AfterViewChecked
{
  readonly dialogRef = inject(MatDialogRef<EditAddUserModalComponent>);
  readonly data: UserModalData = inject(MAT_DIALOG_DATA);
  readonly modalStateService = inject(ModalStateService);
  private readonly store = inject(Store);
  private readonly updates$ = inject(Actions);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly userAdminService = inject(UserAdminService);

  readonly roles$ = this.store.select(selectUserAdminFilteredRolesWithRole);
  readonly orgs$ = this.store.select(selectUserAdminFilteredOrgsWithRole);
  readonly isSaveDisabled$ = this.store.select(selectIsSaveDisabled);
  readonly filteredOrgs$: Observable<Organization[]>;

  readonly orgSearchControl = new FormControl<string>("");
  readonly addUserForm: FormGroup<UserForm>;
  readonly isFormValid$: Observable<boolean>;

  isAdminRole = false;
  hasSaveButtonBeenPressed = false;
  private currentOrgs: Organization[] = [];
  private readonly subs: Subscription[] = [];

  constructor() {
    this.addUserForm = this.createForm();
    this.isFormValid$ = this.addUserForm.statusChanges.pipe(
      map(() => this.isFormValidForSubmission())
    );
    this.filteredOrgs$ = this.createFilteredOrgs();
  }

  ngOnInit(): void {
    this.modalStateService.setModalOpen(true);
    this.initializeStoreData();
    this.setupFormSubscriptions();
    this.setupSuccessHandling();
    this.addAsyncEmailValidator();
    if (this.isProviderFlow) {
      this.handleProviderFlowSetup();
    }
  }

  ngAfterViewInit(): void {
    this.cdr.detectChanges();
  }

  ngAfterViewChecked(): void {
    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this.modalStateService.setModalOpen(false);
    this.subs.forEach(sub => sub?.unsubscribe());
  }

  onRoleChange(event: MatSelectChange): void {
    if (this.isProviderFlow) {
      this.handleProviderRoleChange();
      return;
    }

    this.handleNormalRoleChange(event);
  }

  submitUser(): void {
    if (this.hasSaveButtonBeenPressed || this.addUserForm.invalid) {
      return;
    }

    this.hasSaveButtonBeenPressed = true;
    const organizationIds = this.getOrganizationIds();

    if (this.isEditMode && !this.isProviderFlow) {
      this.updateExistingUser(organizationIds);
    } else {
      this.createNewUser(organizationIds);
    }

    this.resetSaveButton();
  }

  getOrgTooltip(): string {
    const orgValue = this.addUserForm.get("org")?.value;

    if (this.isAdminRole && Array.isArray(orgValue)) {
      return this.getMultiOrgTooltip(orgValue);
    }

    return this.getSingleOrgTooltip(orgValue);
  }

  private get isProviderFlow(): boolean {
    return !!this.data.shouldPopulatedFieldsBeDisabled;
  }

  private get isEditMode(): boolean {
    return !!this.data.user;
  }


  private createForm(): FormGroup<UserForm> {
  return new FormGroup({
    firstName: new FormControl(
      {
        value: this.data.user?.firstName ?? "",
        disabled: this.shouldDisableField("firstName"),
      },
      {
        validators: [Validators.required, Validators.maxLength(35)],
        nonNullable: true,
      }
    ),
    lastName: new FormControl(
      {
        value: this.data.user?.lastName ?? "",
        disabled: this.shouldDisableField("lastName"),
      },
      {
        validators: [Validators.required, Validators.maxLength(35)],
        nonNullable: true,
      }
    ),
    role: new FormControl<string | number | null>(null, [Validators.required]),
    emailAddress: new FormControl(
      {
        value: this.data.user?.emailAddress ?? "",
        disabled: this.isEditMode && !this.isProviderFlow,
      },
      {
        validators: [
          Validators.required,
          Validators.pattern(
            /^[\w]{1,}[\w.+-]{0,}@[\w-]{1,}([.][a-zA-Z]{2,}|[.][\w-]{2,}[.][a-zA-Z]{2,})$/
          ),
          emailLengthValidator(),
        ],
        updateOn: "blur",
        nonNullable: true,
      }
    ),
    org: new FormControl<number | number[] | null>(
      {
        value: null,
        disabled: this.isProviderFlow,
      },
      Validators.required
    ),
    active: new FormControl(
      this.data.user?.active === 1,
      { nonNullable: true }
    ),
  });
}

  private createFilteredOrgs(): Observable<Organization[]> {
    return combineLatest([
      this.orgs$,
      this.orgSearchControl.valueChanges.pipe(startWith("")),
    ]).pipe(
      map(([orgs, searchTerm]) => {
        if (!orgs) return [];

        // Filter to only show active organizations (statusID = 1000)
        let filteredOrgs = orgs.filter((org) => org.statusID === 1000);

        if (!searchTerm) return filteredOrgs;

        return filteredOrgs.filter((org) =>
          org.name.toLowerCase().includes(searchTerm.toLowerCase())
        );
      })
    );
  }

  private shouldDisableField(fieldName: string): boolean {
    return (
      this.isProviderFlow &&
      !!this.data.user?.[fieldName as keyof typeof this.data.user]
    );
  }

  private initializeStoreData(): void {
    this.store.dispatch(
      UserAdminActions.setOriginalEmailForEdit({
        email: this.data?.user?.emailAddress ?? "",
      })
    );
  }

  private setupFormSubscriptions(): void {
    this.subs.push(
      this.orgs$.subscribe(orgs => {
        this.currentOrgs = orgs || [];
        
        if (this.isProviderFlow && orgs?.length && !this.addUserForm.get("org")?.value) {
          this.setProviderOrgValue();
        }
      })
    );

    this.subs.push(
      this.roles$
        .pipe(
          filter(roles => roles !== null && roles.length > 0),
          take(1)
        )
        .subscribe(roles => {
          this.initializeRoleAndOrgs(roles);
          this.cdr.detectChanges();
        })
    );
  }

  private setupSuccessHandling(): void {
    this.subs.push(
      this.updates$
        .pipe(
          ofType(
            UserAdminActions.addUserSuccess,
            ClinicianDetailsActions.createUserFromProviderSuccess,
            UserAdminActions.updateUserSuccess
          )
        )
        .subscribe(() => this.dialogRef.close())
    );
  }

  private handleProviderFlowSetup(): void {
    const fieldsToFix = ["firstName", "lastName"];

    fieldsToFix.forEach(fieldName => {
      const control = this.addUserForm.get(fieldName);
      if (control?.disabled) {
        control.clearValidators();
        control.updateValueAndValidity();
      }
    });

    const orgControl = this.addUserForm.get("org");
    if (orgControl?.disabled) {
      orgControl.clearValidators();
      orgControl.updateValueAndValidity();
    }

    this.addUserForm.updateValueAndValidity();
  }

  private initializeRoleAndOrgs(roles: any[]): void {
    if (!this.data.user?.roleId || this.data.user.roleId === "0") {
      return;
    }

    const matchingRole = roles.find(
      role => role.id.toString() === this.data.user?.roleId
    );

    if (!matchingRole) return;

    this.addUserForm.get("role")?.setValue(matchingRole.id);
    this.isAdminRole = matchingRole.name.toLowerCase() === "admin";

    if (this.data.user?.organizationIds?.length) {
      this.initializeUserOrganizations();
    }
  }

  private initializeUserOrganizations(): void {
    if (!this.data.user?.organizationIds?.length) return;

    if (this.isProviderFlow) {
      if (this.currentOrgs.length > 0) {
        this.setProviderOrgValue();
      }
      return;
    }

    const orgValue = this.isAdminRole
      ? this.data.user.organizationIds
      : this.data.user.organizationIds[0] || null;

    this.addUserForm.get("org")?.setValue(orgValue);

    setTimeout(() => this.cdr.detectChanges(), 50);
  }

  private setProviderOrgValue(): void {
    this.subs.push(
      this.store.select(selectOrgId).pipe(take(1)).subscribe(orgId => {
        if (!orgId) return;

        const orgControl = this.addUserForm.get("org");
        orgControl?.enable();
        orgControl?.setValue(Number(orgId));
        
        setTimeout(() => {
          orgControl?.disable();
          this.cdr.detectChanges();
        }, 0);
      })
    );
  }

  private handleProviderRoleChange(): void {
    this.subs.push(
      this.roles$.pipe(take(1)).subscribe(roles => {
        if (roles) {
          this.isAdminRole = false;
          this.cdr.detectChanges();
        }
      })
    );
  }

  private handleNormalRoleChange(event: MatSelectChange): void {
    this.subs.push(
      this.roles$.pipe(take(1)).subscribe(roles => {
        if (!roles) return;

        const selectedRole = roles.find(
          role => role.id === event.value || role.id.toString() === event.value
        );

        if (!selectedRole) return;

        const wasAdmin = this.isAdminRole;
        const willBeAdmin = selectedRole.name.toLowerCase() === "admin";
        const orgControl = this.addUserForm.get("org");

        if (!orgControl) return;

        if (wasAdmin !== willBeAdmin) {
          this.isAdminRole = willBeAdmin;
          orgControl.setValue(willBeAdmin ? [] : null);
          
          setTimeout(() => {
            orgControl.markAsTouched();
            orgControl.markAsDirty();
            orgControl.updateValueAndValidity();
            this.addUserForm.updateValueAndValidity();
            this.cdr.detectChanges();
          }, 0);
        } else {
          this.isAdminRole = willBeAdmin;
          const currentValue = orgControl.value;
          
          if (willBeAdmin && !Array.isArray(currentValue)) {
            orgControl.setValue([]);
          } else if (!willBeAdmin && Array.isArray(currentValue)) {
            orgControl.setValue(null);
          }
          
          this.cdr.detectChanges();
        }
      })
    );
  }

  private isFormValidForSubmission(): boolean {
    const controls = this.addUserForm.controls;
    
    for (const [key, control] of Object.entries(controls)) {
      if (control.disabled || control.pending) {
        continue;
      }
      
      if (key === "role" && this.isInvalidRoleValue(control.value)) {
        return false;
      }
      
      if (key === "org" && this.isInvalidOrgValue(control.value)) {
        return false;
      }
      
      if (control.invalid) {
        return false;
      }
    }
    return true;
  }

  private isInvalidRoleValue(value: any): boolean {
    return value === null || value === undefined || value === "" || value === "0";
  }

  private isInvalidOrgValue(value: any): boolean {
    if (this.isAdminRole) {
      return !Array.isArray(value) || value.length === 0;
    }
    return value === null || value === undefined || value === "";
  }

  private getMultiOrgTooltip(orgValues: number[]): string {
    if (orgValues.length === 0) return "";
    
    const orgNames = orgValues
      .map(id => {
        const org = this.currentOrgs.find(o => Number(o.id) === Number(id));
        return org ? org.name : `Unknown (ID: ${id})`;
      })
      .join(", ");
    
    return orgNames;
  }

  private getSingleOrgTooltip(orgValue: any): string {
    if (orgValue === null || orgValue === undefined) return "";
    
    const org = this.currentOrgs.find(o => Number(o.id) === Number(orgValue));
    return org ? org.name : `Unknown (ID: ${orgValue})`;
  }

  private getOrganizationIds(): number[] {
    const orgValue = this.addUserForm.get("org")?.value;

    if (this.isAdminRole && Array.isArray(orgValue)) {
      return orgValue;
    }
    
    return typeof orgValue === "number" ? [orgValue] : [];
  }

  private updateExistingUser(organizationIds: number[]): void {
    if (!this.data.user) return;

    const updatedUser: User = {
      id: this.data.user.id,
      isSuperAdmin: this.data.user.isSuperAdmin,
      userName: this.addUserForm.get("emailAddress")?.value ?? "",
      active: this.addUserForm.get("active")?.value ? 1 : 0,
      b2cID: this.data.user.b2cID ?? "",
      firstName: this.addUserForm.get("firstName")?.value ?? "",
      lastName: this.addUserForm.get("lastName")?.value ?? "",
      emailAddress: this.addUserForm.get("emailAddress")?.value ?? "",
      roleId: this.addUserForm.get("role")?.value?.toString() ?? "",
      organizationIds,
      lastLogin: this.data.user.lastLogin,
    };

    this.store.dispatch(UserAdminActions.updateUser({ user: updatedUser }));
  }

  private createNewUser(organizationIds: number[]): void {
    const newUser: User = {
      id: "",
      isSuperAdmin: 0,
      userName: this.addUserForm.get("emailAddress")?.value ?? "",
      active: 1,
      b2cID: "",
      firstName: this.addUserForm.get("firstName")?.value ?? "",
      lastName: this.addUserForm.get("lastName")?.value ?? "",
      emailAddress: this.addUserForm.get("emailAddress")?.value ?? "",
      roleId: this.addUserForm.get("role")?.value?.toString() ?? "",
      organizationIds,
    };

    if (this.isProviderFlow) {
      this.store.dispatch(
        ClinicianDetailsActions.createUserFromProvider({
          userData: {
            ...newUser,
            providerId: this.data.providerId || 0,
          },
          organizationProviderId: this.data.organizationProviderId || 0,
        })
      );
    } else {
      this.store.dispatch(UserAdminActions.addUser({ user: newUser }));
    }
  }

  private resetSaveButton(): void {
    setTimeout(() => {
      this.hasSaveButtonBeenPressed = false;
    }, 3000);
  }

  private addAsyncEmailValidator(): void {
  if (this.isEditMode && !this.isProviderFlow) {
    return;
  }

  setTimeout(() => {
    const emailControl = this.addUserForm.get('emailAddress');
    if (emailControl) {
      emailControl.setAsyncValidators([
        uniqueEmailValidator(
          this.data.user?.emailAddress ?? "",
          this.userAdminService
        )
      ]);
    }
  }, 0);
}

  resetPassword() {
    if (!this.data.user) return;

    this.store.dispatch(UserAdminActions.resetUserPasswordPressed({ userId: this.data.user.id }))
  }

}
